import {
    UtmSourceEnum,
    UtmMediumEnum,
    UtmContentEnum,
} from '@/Shared/Service/Tracking/Utm/UtmEnums'
import { UtmParameters } from '@/Shared/Service/Tracking/Utm/UtmParameters'
import { UtmUrlBuilder } from '@/Shared/Service/Tracking/Utm/UtmUrlBuilder'
import AttendanceID from '@/App/Booking/Attendance/Entity/AttendanceID'
import BookingID from '@/App/Booking/Booking/Entity/BookingID'
import MeetingID from '@/App/Booking/Meeting/Entity/MeetingID'
import ReservationID from '@/App/Booking/Reservation/Entity/ReservationID'
import CompanyUserID from '@/App/CompanyUser/Entity/CompanyUserID'
import CustomFieldID from '@/App/CustomField/Entity/CustomFieldID'
import IntegrationID from '@/App/Integration/Entity/IntegrationID'
import ServiceID from '@/App/Service/Entity/ServiceID'
import ServicePriceID from '@/App/Service/Entity/ServicePriceID'
import AvailabilityID from '@/App/Availability/Entity/AvailabilityID'
import AutomationID from '@/App/Automation/Entity/AutomationID'
import {
    EditServiceOriginEnum,
    MeetingSearchFiltersDateTypeEnum,
    UserSocialAuthenticationActionEnum,
    type MeetingSearchFiltersType,
    type RecurringMeetingSearchFiltersType,
} from '@/Shared/AutoGenerated/Main'
import {
    RequestQueryKeyEnum,
    RouteParamKeyEnum,
} from '@/Shared/AutoGenerated/RequestFieldEnum'
import {
    RoutePathAppEnum,
    RoutePathBookingFormEnum,
    RoutePathCustomerEnum,
    RoutePathFreeToolEnum,
    RoutePathMarketingEnum,
    RoutePathMeetingEnum,
} from '@/Shared/AutoGenerated/RoutePathEnum'
import useUrlGenerator from '@/Shared/Hook/useUrlGenerator'
import { AbstractUrlFactory } from '@/Shared/Url/AbstractUrlFactory'

export class UrlFactory extends AbstractUrlFactory {
    // -----------------------------------
    // DOMAIN: App/Automation
    // -----------------------------------

    public makeAutomationStoreUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Automations_Store)
    }

    public makeAutomationsUrl(perPage: number): string {
        return useUrlGenerator().app(RoutePathAppEnum.Automations, [
            { key: RequestQueryKeyEnum.PaginationPerPage, value: perPage },
        ])
    }

    public makeAutomationDeleteUrl(automationId: AutomationID): string {
        return this.makeAutomationUrl(
            RoutePathAppEnum.Automations_Delete,
            automationId,
        )
    }

    public makeAutomationUpdateStateUrl(automationId: AutomationID): string {
        return this.makeAutomationUrl(
            RoutePathAppEnum.Automations_Update_State,
            automationId,
        )
    }

    public makeAutomationUpdateGeneralSettingsUrl(
        automationId: AutomationID,
    ): string {
        return this.makeAutomationUrl(
            RoutePathAppEnum.Automations_Update_GeneralSettings,
            automationId,
        )
    }

    public makeAutomationUpdateActionSettingsUrl(
        automationId: AutomationID,
    ): string {
        return this.makeAutomationUrl(
            RoutePathAppEnum.Automations_Update_Action,
            automationId,
        )
    }

    public makeAutomationUpdateTriggerSettingsUrl(
        automationId: AutomationID,
    ): string {
        return this.makeAutomationUrl(
            RoutePathAppEnum.Automations_Update_Trigger,
            automationId,
        )
    }

    private makeAutomationUrl(
        route: RoutePathAppEnum,
        automationId: AutomationID,
    ): string {
        return useUrlGenerator().app(route, [
            {
                key: RequestQueryKeyEnum.AutomationId,
                value: automationId.value,
            },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/Availability
    // -----------------------------------

    public makeAvailabilityStoreUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Availabilities_Store)
    }

    public makeAvailabilityUpdateDetailsUrl(
        availabilityId: AvailabilityID,
    ): string {
        return this.makeAvailabilityUrl(
            RoutePathAppEnum.Availabilities_Update_Details,
            availabilityId,
        )
    }

    public makeAvailabilityUpdateWeeklyHoursUrl(
        availabilityId: AvailabilityID,
    ): string {
        return this.makeAvailabilityUrl(
            RoutePathAppEnum.Availabilities_Update_WeeklyHours,
            availabilityId,
        )
    }

    public makeAvailabilityUpdateDateSpecificHoursUrl(
        availabilityId: AvailabilityID,
    ): string {
        return this.makeAvailabilityUrl(
            RoutePathAppEnum.Availabilities_Update_DateSpecificHours,
            availabilityId,
        )
    }

    public makeAvailabilityUpdateAiUrl(availabilityId: AvailabilityID): string {
        return this.makeAvailabilityUrl(
            RoutePathAppEnum.Availabilities_Update_Ai,
            availabilityId,
        )
    }

    public makeAvailabilityDeleteUrl(availabilityId: AvailabilityID): string {
        return this.makeAvailabilityUrl(
            RoutePathAppEnum.Availabilities_Delete,
            availabilityId,
        )
    }

    public makeAvailabilityDefaultUrl(availabilityId: AvailabilityID): string {
        return this.makeAvailabilityUrl(
            RoutePathAppEnum.Company_Update_DefaultAvailability,
            availabilityId,
        )
    }

    private makeAvailabilityUrl(
        route: RoutePathAppEnum,
        availabilityId: AvailabilityID,
    ): string {
        return useUrlGenerator().app(route, [
            {
                key: RequestQueryKeyEnum.AvailabilityId,
                value: availabilityId.value,
            },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/Booking
    // -----------------------------------

    public makeMeetingAttendanceRescheduleUrl(
        meetingId: MeetingID,
        attendanceId: AttendanceID,
    ): string {
        return this.makeMeetingAttendanceUrl(
            RoutePathMeetingEnum.Meetings_Reschedule,
            meetingId,
            attendanceId,
        )
    }

    public makeMeetingAttendanceCancelUrl(
        meetingId: MeetingID,
        attendanceId: AttendanceID,
    ): string {
        return this.makeMeetingAttendanceUrl(
            RoutePathMeetingEnum.Meetings_Cancel,
            meetingId,
            attendanceId,
        )
    }

    public makeMeetingAttendanceToggleNoShowUrl(
        meetingId: MeetingID,
        attendanceId: AttendanceID,
    ): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Meetings_Attendee_ToggleNoShow,
            this.makeMeetingAttendanceQueryParams(meetingId, attendanceId),
        )
    }

    public makeRecurringMeetingRefundUrl(bookingId: BookingID): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Meetings_Payment_Refund_Recurring,
            [{ key: RequestQueryKeyEnum.BookingId, value: bookingId.value }],
        )
    }

    public makeRecurringMeetingsUrl(
        filters: RecurringMeetingSearchFiltersType,
    ): string {
        return useUrlGenerator().app(RoutePathAppEnum.Recurring, [
            {
                key: RequestQueryKeyEnum.PaginationPerPage,
                value: filters.perPage,
            },
            {
                key: RequestQueryKeyEnum.App_IndexRecurringMeetings_Filter_Search,
                value: filters.search ?? '',
            },
        ])
    }

    public makeRecurringMeetingCancelUrl(bookingId: BookingID): string {
        return useUrlGenerator().meeting(
            RoutePathMeetingEnum.Meetings_Recurring_Cancel,
            [{ key: RequestQueryKeyEnum.BookingId, value: bookingId.value }],
        )
    }

    public makeMeetingRescheduleUrl(meetingId: MeetingID): string {
        return useUrlGenerator().meeting(
            RoutePathMeetingEnum.Meetings_Reschedule,
            [{ key: RequestQueryKeyEnum.MeetingId, value: meetingId.value }],
        )
    }

    public makeMeetingAvailableDatesUrl(
        meetingId: MeetingID,
        timeZone: string,
    ): string {
        return useUrlGenerator().meeting(RoutePathMeetingEnum.Meetings_Dates, [
            { key: RequestQueryKeyEnum.MeetingId, value: meetingId.value },
            {
                key: RequestQueryKeyEnum.AvailableDates_TimeZone,
                value: timeZone,
            },
        ])
    }

    public makeMeetingsUrl(filters: MeetingSearchFiltersType): string {
        return useUrlGenerator().app(RoutePathAppEnum.Meetings, [
            {
                key: RequestQueryKeyEnum.PaginationPerPage,
                value: filters.perPage,
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_ServiceId,
                value: filters.serviceId ?? '',
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_CompanyUserId,
                value: filters.companyUserId ?? '',
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_MeetingState,
                value: filters.meetingState ?? '',
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_DateType,
                value: filters.dateType ?? '',
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_MeetingStartAt,
                value: filters.meetingStartAtInAvailabilityTimeZone ?? '',
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_MeetingEndAt,
                value: filters.meetingEndAtInAvailabilityTimeZone ?? '',
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_Search,
                value: filters.search ?? '',
            },
        ])
    }

    public makeMeetingIndexByUniqueCodeUrl(uniqueCode: string): string {
        return useUrlGenerator().app(RoutePathAppEnum.Meetings, [
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_Search,
                value: uniqueCode,
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_DateType,
                value: MeetingSearchFiltersDateTypeEnum.All,
            },
        ])
    }

    public makeNonRecurringMeetingRefundUrl(
        meetingId: MeetingID,
        reservationId: ReservationID,
    ): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Meetings_Payment_Refund_NonRecurring,
            [
                { key: RequestQueryKeyEnum.MeetingId, value: meetingId.value },
                {
                    key: RequestQueryKeyEnum.ReservationId,
                    value: reservationId.value,
                },
            ],
        )
    }

    private makeMeetingAttendanceUrl(
        route: RoutePathMeetingEnum,
        meetingId: MeetingID,
        attendanceId: AttendanceID,
    ): string {
        return useUrlGenerator().meeting(
            route,
            this.makeMeetingAttendanceQueryParams(meetingId, attendanceId),
        )
    }

    private makeMeetingAttendanceQueryParams(
        meetingId: MeetingID,
        attendanceId: AttendanceID,
    ) {
        return [
            { key: RequestQueryKeyEnum.MeetingId, value: meetingId.value },
            {
                key: RequestQueryKeyEnum.AttendanceId,
                value: attendanceId.value,
            },
        ]
    }

    // -----------------------------------
    // DOMAIN: App/Company
    // -----------------------------------

    public makeCompanyMeetingLocationStoreUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Company_MeetingLocations_Store,
        )
    }

    public makeCompanyMeetingLocationDeleteUrl(locationId: string): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Company_MeetingLocations_Delete,
            [
                {
                    key: RequestQueryKeyEnum.InPersonHostDefinedMeetingLocationId,
                    value: locationId,
                },
            ],
        )
    }

    public makeCompanyEditUrl(append = ''): string {
        return useUrlGenerator().app(RoutePathAppEnum.Company) + append
    }

    public makeCompanyUpdateEmbedButtonSettingsUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Company_Update_EmbedButtonSettings,
        )
    }

    // -----------------------------------
    // DOMAIN: App/CompanyUser
    // -----------------------------------

    public makeCompanyUserAssociateAvailabilityUrl(
        companyUserId: CompanyUserID,
    ): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.CompanyUsers_Availability_Associate,
            [
                {
                    key: RequestQueryKeyEnum.CompanyUserId,
                    value: companyUserId.value,
                },
            ],
        )
    }

    public makeCompanyUserUpdateServicesUrl(
        companyUserId: CompanyUserID,
        origin: EditServiceOriginEnum,
    ): string {
        if (origin === EditServiceOriginEnum.ProfilePage) {
            return useUrlGenerator().app(
                RoutePathAppEnum.MyProfile_Services_Associate,
            )
        }

        if (origin === EditServiceOriginEnum.CompanyUserPage) {
            return useUrlGenerator().app(
                RoutePathAppEnum.CompanyUsers_Services_Associate,
                [
                    {
                        key: RequestQueryKeyEnum.CompanyUserId,
                        value: companyUserId.value,
                    },
                ],
            )
        }

        throw new Error(`Unhandled origin '${origin}'`)
    }

    // -----------------------------------
    // DOMAIN: App/Integration
    // -----------------------------------

    public makeIntegrationUpdateGoogleCalendarSettingsUrl(
        integrationId: IntegrationID,
    ): string {
        return this.makeIntegrationSettingsUrl(
            RoutePathAppEnum.Integrations_GoogleCalendar_Update_Settings,
            integrationId,
        )
    }

    public makeIntegrationUpdateOutlookCalendarSettingsUrl(
        integrationId: IntegrationID,
    ): string {
        return this.makeIntegrationSettingsUrl(
            RoutePathAppEnum.Integrations_OutlookCalendar_Update_Settings,
            integrationId,
        )
    }

    private makeIntegrationSettingsUrl(
        route: RoutePathAppEnum,
        integrationId: IntegrationID,
    ): string {
        return useUrlGenerator().app(route, [
            {
                key: RequestQueryKeyEnum.IntegrationId,
                value: integrationId.value,
            },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/Service
    // -----------------------------------

    public makeServiceUpdateGeneralSettingsUrl(serviceId: ServiceID): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_Update_GeneralSettings,
            serviceId,
        )
    }

    public makeServiceUpdateSchedulingSettingsUrl(
        serviceId: ServiceID,
    ): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_Update_SchedulingSettings,
            serviceId,
        )
    }

    public makeServiceUpdatePaymentSettingsUrl(serviceId: ServiceID): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_Update_PaymentSettings,
            serviceId,
        )
    }

    public makeServiceUpdateRecurrenceSettingsUrl(
        serviceId: ServiceID,
    ): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_Update_RecurrenceSettings,
            serviceId,
        )
    }

    public makeServiceUpdateGroupSchedulingSettingsUrl(
        serviceId: ServiceID,
    ): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_Update_GroupSchedulingSettings,
            serviceId,
        )
    }

    public makeServiceDeleteUrl(serviceId: ServiceID): string {
        return this.makeServiceUrl(RoutePathAppEnum.Services_Delete, serviceId)
    }

    public makeServiceAssociateCompanyUsersUrl(serviceId: ServiceID): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_CompanyUsers_Associate,
            serviceId,
        )
    }

    public makeServicePriceStoreUrl(serviceId: ServiceID): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_Prices_Store,
            serviceId,
        )
    }

    public makeServiceCompanyUsersUrl(serviceId: ServiceID): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_CompanyUsers,
            serviceId,
        )
    }

    public makeServiceCustomFieldStoreUrl(serviceId: ServiceID): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_CustomFields_Store,
            serviceId,
        )
    }

    public makeServiceAssociateAvailabilityUrl(serviceId: ServiceID): string {
        return this.makeServiceUrl(
            RoutePathAppEnum.Services_Availability_Associate,
            serviceId,
        )
    }

    public makeServicePriceUpdateUrl(
        serviceId: ServiceID,
        servicePriceId: ServicePriceID,
    ): string {
        return this.makeServicePriceUrl(
            RoutePathAppEnum.Services_Prices_Update,
            serviceId,
            servicePriceId,
        )
    }

    public makeServicePriceDeleteUrl(
        serviceId: ServiceID,
        servicePriceId: ServicePriceID,
    ): string {
        return this.makeServicePriceUrl(
            RoutePathAppEnum.Services_Prices_Delete,
            serviceId,
            servicePriceId,
        )
    }

    public makeServiceCustomFieldUpdateUrl(
        serviceId: ServiceID,
        customFieldId: CustomFieldID,
    ): string {
        return this.makeServiceCustomFieldUrl(
            RoutePathAppEnum.Services_CustomFields_Update,
            serviceId,
            customFieldId,
        )
    }

    public makeServiceCustomFieldDeleteUrl(
        serviceId: ServiceID,
        customFieldId: CustomFieldID,
    ): string {
        return this.makeServiceCustomFieldUrl(
            RoutePathAppEnum.Services_CustomFields_Delete,
            serviceId,
            customFieldId,
        )
    }

    public makeServiceCustomFieldMoveUpUrl(
        serviceId: ServiceID,
        customFieldId: CustomFieldID,
    ): string {
        return this.makeServiceCustomFieldUrl(
            RoutePathAppEnum.Services_CustomFields_MoveUp,
            serviceId,
            customFieldId,
        )
    }

    public makeServiceCustomFieldMoveDownUrl(
        serviceId: ServiceID,
        customFieldId: CustomFieldID,
    ): string {
        return this.makeServiceCustomFieldUrl(
            RoutePathAppEnum.Services_CustomFields_MoveDown,
            serviceId,
            customFieldId,
        )
    }

    private makeServiceUrl(
        route: RoutePathAppEnum,
        serviceId: ServiceID,
    ): string {
        return useUrlGenerator().app(route, [
            { key: RequestQueryKeyEnum.ServiceId, value: serviceId.value },
        ])
    }

    private makeServicePriceUrl(
        route: RoutePathAppEnum,
        serviceId: ServiceID,
        servicePriceId: ServicePriceID,
    ): string {
        return useUrlGenerator().app(route, [
            { key: RequestQueryKeyEnum.ServiceId, value: serviceId.value },
            {
                key: RequestQueryKeyEnum.ServicePriceId,
                value: servicePriceId.value,
            },
        ])
    }

    private makeServiceCustomFieldUrl(
        route: RoutePathAppEnum,
        serviceId: ServiceID,
        customFieldId: CustomFieldID,
    ): string {
        return useUrlGenerator().app(route, [
            { key: RequestQueryKeyEnum.ServiceId, value: serviceId.value },
            {
                key: RequestQueryKeyEnum.CustomFieldId,
                value: customFieldId.value,
            },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/User
    // -----------------------------------

    public makeGoogleOAuthLoginRedirectUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Auth_User_Social_Google_Redirect,
            [
                {
                    key: RequestQueryKeyEnum.OAuth_AuthenticationAction,
                    value: UserSocialAuthenticationActionEnum.Login,
                },
            ],
        )
    }

    public makeGoogleOAuthRegisterRedirectUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Auth_User_Social_Google_Redirect,
            [
                {
                    key: RequestQueryKeyEnum.OAuth_AuthenticationAction,
                    value: UserSocialAuthenticationActionEnum.Register,
                },
            ],
        )
    }

    public makeMicrosoftOAuthLoginRedirectUrl(): string {
        return this.makeMicrosoftOAuthRedirectUrl(
            UserSocialAuthenticationActionEnum.Login,
        )
    }

    public makeMicrosoftOAuthRegisterRedirectUrl(): string {
        return this.makeMicrosoftOAuthRedirectUrl(
            UserSocialAuthenticationActionEnum.Register,
        )
    }

    public makeOnboardingUpdateCustomAvailabilityUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Onboarding_Step4_Availability_Custom,
        )
    }

    public makeOnboardingUpdatePredefinedAvailabilityUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Onboarding_Step4_Availability_PreDefined,
        )
    }

    private makeMicrosoftOAuthRedirectUrl(
        authenticationAction: UserSocialAuthenticationActionEnum,
    ): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Auth_User_Social_Microsoft_Redirect,
            [
                {
                    key: RequestQueryKeyEnum.OAuth_AuthenticationAction,
                    value: authenticationAction,
                },
            ],
        )
    }

    // -----------------------------------
    // DOMAIN: BookingForm/BookingForm
    // -----------------------------------

    public makeCompanyBookingFormUrl(companySlug: string): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_Service,
            [],
            [{ key: RouteParamKeyEnum.CompanySlug, value: companySlug }],
        )
    }

    public makeBookingFormCompanyUserSelectionUrl(
        companySlug: string,
        serviceSlug: string,
        draftMeetingId: number,
        companyUserId: CompanyUserID,
        locale: string,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_CompanyUser,
            [
                {
                    key: RequestQueryKeyEnum.DraftMeetingId,
                    value: draftMeetingId,
                },
                {
                    key: RequestQueryKeyEnum.CompanyUserId,
                    value: companyUserId.value,
                },
                { key: RequestQueryKeyEnum.Locale, value: locale },
            ],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingFormServiceUrl(
        companySlug: string,
        serviceSlug: string,
        locale: string,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_CompanyUser,
            [{ key: RequestQueryKeyEnum.Locale, value: locale }],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingConfirmationSubmitUrl(
        route: RoutePathBookingFormEnum,
        companySlug: string,
        serviceSlug: string,
        draftMeetingId: number,
        locale: string,
    ): string {
        return useUrlGenerator().bookingForm(
            route,
            [
                {
                    key: RequestQueryKeyEnum.DraftMeetingId,
                    value: draftMeetingId,
                },
                { key: RequestQueryKeyEnum.Locale, value: locale },
            ],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingFormDateUrl(
        companySlug: string,
        serviceSlug: string,
        draftMeetingId: number,
        timeZone: string,
        dateInSelectedTimeZone: string,
        startInSelectedTimeZone: string,
        endInSelectedTimeZone: string,
        dateInAvailabilityTimeZone: string,
        startInAvailabilityTimeZone: string,
        endInAvailabilityTimeZone: string,
        locale: string,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_Date,
            [
                {
                    key: RequestQueryKeyEnum.DraftMeetingId,
                    value: draftMeetingId,
                },
                {
                    key: RequestQueryKeyEnum.AvailableDates_TimeZone,
                    value: timeZone,
                },
                {
                    key: RequestQueryKeyEnum.AvailableDates_DateInSelectedTimeZoneForDisplay,
                    value: dateInSelectedTimeZone,
                },
                {
                    key: RequestQueryKeyEnum.AvailableDates_TimeSlotStartInSelectedTimeZoneForDisplay,
                    value: startInSelectedTimeZone,
                },
                {
                    key: RequestQueryKeyEnum.AvailableDates_TimeSlotEndInSelectedTimeZoneForDisplay,
                    value: endInSelectedTimeZone,
                },
                {
                    key: RequestQueryKeyEnum.AvailableDates_DateInAvailabilityTimeZoneForScheduling,
                    value: dateInAvailabilityTimeZone,
                },
                {
                    key: RequestQueryKeyEnum.AvailableDates_TimeSlotStartInAvailabilityTimeZoneForScheduling,
                    value: startInAvailabilityTimeZone,
                },
                {
                    key: RequestQueryKeyEnum.AvailableDates_TimeSlotEndInAvailabilityTimeZoneForScheduling,
                    value: endInAvailabilityTimeZone,
                },
                { key: RequestQueryKeyEnum.Locale, value: locale },
            ],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingFormAvailableDatesUrl(
        companySlug: string,
        serviceSlug: string,
        draftMeetingId: number,
        timeZone: string,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_Date_AvailableDates,
            [
                {
                    key: RequestQueryKeyEnum.DraftMeetingId,
                    value: draftMeetingId,
                },
                {
                    key: RequestQueryKeyEnum.AvailableDates_TimeZone,
                    value: timeZone,
                },
            ],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    // -----------------------------------
    // DOMAIN: BookingForm/Customer
    // -----------------------------------

    public makeBookingFormCustomerLoginUrl(): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Auth_Customer_Login,
        )
    }

    // -----------------------------------
    // DOMAIN: Customer/Auth
    // -----------------------------------

    public makeCustomerLoginUrl(): string {
        return useUrlGenerator().customer(RoutePathCustomerEnum.Auth_Login)
    }

    public makeCustomerPasswordResetUrl(): string {
        return useUrlGenerator().customer(
            RoutePathCustomerEnum.Auth_PasswordReset,
        )
    }

    public makeCustomerGoogleOAuthRedirectUrl(currentUrl?: string): string {
        const queryParams = currentUrl
            ? [
                  {
                      key: RequestQueryKeyEnum.Customer_Auth_RedirectUrl,
                      value: currentUrl,
                  },
              ]
            : []

        return useUrlGenerator().customer(
            RoutePathCustomerEnum.Auth_Customer_Social_Google_Redirect,
            queryParams,
        )
    }

    // -----------------------------------
    // DOMAIN: Customer/Meeting
    // -----------------------------------

    public makeCompanyCustomerMeetingsUrl(companyId: number): string {
        return useUrlGenerator().customer(RoutePathCustomerEnum.Meetings, [
            { key: RequestQueryKeyEnum.CompanyId, value: companyId },
        ])
    }

    // -----------------------------------
    // DOMAIN: FreeTool/BusinessNameGenerator
    // -----------------------------------

    public makeGoogleOAuthBusinessNameGeneratorRegisterRedirectUrl(): string {
        return useUrlGenerator().freeTool(
            RoutePathFreeToolEnum.BusinessNameGenerator_StepRegister_GoogleRedirect,
        )
    }

    public makeMicrosoftOAuthBusinessNameGeneratorRegisterRedirectUrl(): string {
        return useUrlGenerator().freeTool(
            RoutePathFreeToolEnum.BusinessNameGenerator_StepRegister_MicrosoftRedirect,
        )
    }

    // -----------------------------------
    // DOMAIN: Marketing/LandingPage
    // -----------------------------------

    public makeLandingPageRegisterUrl(content: UtmContentEnum): string {
        return new UtmUrlBuilder().build(
            this.makeAuthRegisterPageUrl(),
            new UtmParameters(
                UtmSourceEnum.LandingPage,
                UtmMediumEnum.Website,
                content,
            ),
        )
    }

    public makeLandingDemoSectionPageUrl(): string {
        return (
            useUrlGenerator().landingPage(RoutePathMarketingEnum.Root) + '#demo'
        )
    }

    public makeLandingFeaturesSectionPageUrl(): string {
        return (
            useUrlGenerator().landingPage(RoutePathMarketingEnum.Root) +
            '#features'
        )
    }

    public makeLandingPricingSectionPageUrl(): string {
        return (
            useUrlGenerator().landingPage(RoutePathMarketingEnum.Root) +
            '#pricing'
        )
    }

    public makeLandingFaqSectionPageUrl(): string {
        return (
            useUrlGenerator().landingPage(RoutePathMarketingEnum.Root) + '#faq'
        )
    }
}
